import { useState, useEffect } from "react";
import { AlertTriangle, Home, Terminal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";

export default function NotFoundPage({ onGoHome }) {
  const [glitchText, setGlitchText] = useState("404");
  const [typedPath, setTypedPath] = useState("");
  const currentPath = window.location.hash || "#/";
  const { t } = useI18n();

  useEffect(() => {
    const chars = "!@#$%^&*()_+-=[]{}|;:',.<>?/~`";
    let count = 0;
    const interval = setInterval(() => {
      if (count < 6) {
        setGlitchText(
          "404".split("").map(c => Math.random() > 0.5 ? chars[Math.floor(Math.random() * chars.length)] : c).join("")
        );
        count++;
      } else {
        setGlitchText("404");
        count = 0;
      }
    }, 150);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let i = 0;
    const interval = setInterval(() => {
      if (i <= currentPath.length) {
        setTypedPath(currentPath.slice(0, i));
        i++;
      } else {
        clearInterval(interval);
      }
    }, 80);
    return () => clearInterval(interval);
  }, [currentPath]);

  return (
    <div className="fixed inset-0 bg-zinc-950 flex flex-col items-center justify-center px-4 overflow-hidden grid-background">
      <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-10">
        <div className="scan-line w-full h-[2px] bg-cyan-400" />
      </div>

      <div className="text-center space-y-8 max-w-lg animate-fade-in-up relative z-10">
        <div className="relative">
          <h1 className="text-8xl sm:text-9xl font-mono font-bold text-cyan-400 glow-text tracking-tighter select-none">
            {glitchText}
          </h1>
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-transparent via-cyan-500 to-transparent" />
        </div>

        <div className="w-16 h-16 mx-auto rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center">
          <AlertTriangle className="w-8 h-8 text-red-400" />
        </div>

        <div className="space-y-3">
          <h2 className="text-xl sm:text-2xl font-mono font-bold text-white">
            {t.routeNotFound}
          </h2>
          <p className="text-zinc-400 font-mono text-sm leading-relaxed">
            {t.pageNotExists}
          </p>
        </div>

        <div className="bg-black/80 border border-zinc-800 p-4 text-left font-mono text-sm max-w-sm mx-auto">
          <div className="flex items-center gap-2 mb-2 text-zinc-600">
            <Terminal className="w-3.5 h-3.5" />
            <span className="text-xs uppercase tracking-widest">{t.terminal}</span>
          </div>
          <div className="text-red-400">
            <span className="text-zinc-500">$ curl </span>
            <span>{typedPath}</span>
            <span className="cursor-blink text-cyan-400">|</span>
          </div>
          <div className="text-red-400 mt-1 text-xs">
            Error: ROUTE_NOT_FOUND
          </div>
        </div>

        <Button
          onClick={onGoHome}
          className="btn-cyber rounded-none border-2 border-cyan-500 bg-transparent text-cyan-400 hover:bg-cyan-500 hover:text-black font-mono uppercase tracking-widest px-8 py-5 text-sm transition-colors duration-200"
        >
          <Home className="w-4 h-4 mr-2" />
          {t.backHome}
        </Button>

        <div className="pt-4">
          <div className="w-12 h-0.5 bg-cyan-500/30 mx-auto mb-4" />
          <div className="flex items-center justify-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-600/20 border border-cyan-500/30 flex items-center justify-center overflow-hidden">
              <img src="/icons/icon-96.png" alt="" className="w-4 h-4 object-contain" onError={(e) => { e.target.style.display = "none"; }} />
            </div>
            <p className="font-mono text-xs font-semibold">
              <span className="text-zinc-600">&lt;</span>
              <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">ShadowMail</span>
              <span className="text-zinc-600"> /&gt;</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
